<script setup>
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  customTools: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['action']);

const { t } = useI18n();

const formatDate = dateString => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const truncate = (text, length = 100) => {
  if (!text) return '-';
  return text.length > length ? `${text.substring(0, length)}...` : text;
};

const getAuthTypeLabel = authType => {
  if (!authType || authType === 'none')
    return t('CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_TYPES.NONE');
  const authTypeUpper = authType.toUpperCase().replace('-', '_');
  return t(`CAPTAIN.CUSTOM_TOOLS.FORM.AUTH_TYPES.${authTypeUpper}`);
};

const handleEdit = tool => {
  emit('action', { action: 'edit', id: tool.id });
};

const handleDelete = tool => {
  emit('action', { action: 'delete', id: tool.id });
};
</script>

<template>
  <div
    class="overflow-hidden border rounded-lg border-n-weak/70 bg-n-solid-1 shadow-sm min-h-[400px]"
  >
    <table class="w-full">
      <thead class="bg-n-alpha-1/50 border-b border-n-weak/60">
        <tr>
          <th
            class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
          >
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-wrench size-3.5 opacity-40" />
              <span>{{ t('CAPTAIN.CUSTOM_TOOLS.TABLE.NAME') }}</span>
            </div>
          </th>
          <th
            class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
          >
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-text size-3.5 opacity-40" />
              <span>{{ t('CAPTAIN.CUSTOM_TOOLS.TABLE.DESCRIPTION') }}</span>
            </div>
          </th>
          <th
            class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
          >
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-shield size-3.5 opacity-40" />
              <span>{{ t('CAPTAIN.CUSTOM_TOOLS.TABLE.AUTH_TYPE') }}</span>
            </div>
          </th>
          <th
            class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
          >
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-calendar size-3.5 opacity-40" />
              <span>{{ t('CAPTAIN.CUSTOM_TOOLS.TABLE.CREATED') }}</span>
            </div>
          </th>
          <th
            class="px-5 py-3 text-xs font-semibold text-right tracking-wide text-n-slate-11"
          >
            <div class="flex items-center justify-end gap-1.5">
              <span>{{ t('CAPTAIN.CUSTOM_TOOLS.TABLE.ACTIONS') }}</span>
              <span class="i-lucide-more-horizontal size-3.5 opacity-30" />
            </div>
          </th>
        </tr>
      </thead>
      <tbody class="bg-n-solid-1">
        <tr
          v-for="tool in customTools"
          :key="tool.id"
          class="transition-all duration-150 ease-out border-t border-n-weak/40 hover:bg-n-blue-2/50 group"
        >
          <td class="px-5 py-3">
            <span class="text-sm font-medium text-n-slate-12 block">
              {{ tool.title || tool.name || '-' }}
            </span>
          </td>
          <td class="px-5 py-3">
            <span class="text-sm text-n-slate-10 line-clamp-2">
              {{ truncate(tool.description, 100) }}
            </span>
          </td>
          <td class="px-5 py-3">
            <span
              class="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-n-alpha-2 text-n-slate-11"
            >
              {{ getAuthTypeLabel(tool.authType || tool.auth_type) }}
            </span>
          </td>
          <td class="px-5 py-3 text-sm text-n-slate-10">
            {{ formatDate(tool.createdAt || tool.created_at) }}
          </td>
          <td class="px-5 py-3">
            <div class="flex justify-end gap-2">
              <Button
                icon="i-lucide-edit"
                variant="ghost"
                size="sm"
                color="blue"
                class="!w-8 !h-8"
                :title="t('CAPTAIN.CUSTOM_TOOLS.TABLE.EDIT')"
                @click="handleEdit(tool)"
              />
              <Button
                icon="i-lucide-trash-2"
                variant="ghost"
                size="sm"
                color="ruby"
                class="!w-8 !h-8"
                :title="t('CAPTAIN.CUSTOM_TOOLS.TABLE.DELETE')"
                @click="handleDelete(tool)"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
